/**
 * The smart-filter editor, shared by the group and the destination-link forms.
 *
 * Shared rather than copied, deliberately. Filters exist in two places in the
 * routing model — on a group and on each link within it — and a second copy of
 * this editor is precisely the duplication that has bitten this codebase
 * before: the previous UI had eight pasted filter dropdowns and they drifted
 * from what the engine implements.
 *
 * There is no list of filter types or operators in this file. The types, the
 * config key each one writes, the operators each key accepts and their
 * defaults all come from `GET /api/v1/meta/enums`. A filter naming an operator
 * the engine does not recognise fails closed, so a stale copy here turns leads
 * away permanently with no error surfaced anywhere.
 *
 * A field filter on a field someone has described (a label and a type) is
 * built with help: the field is picked by label, the conditions are the ones its type
 * suits, and the value is a dropdown of its options, yes or no, a number or
 * a date. What is written is the same `{field, operator, value}` text as
 * before, and the engine still compares the raw value the lead arrived
 * with, so routing behaves exactly as it did.
 *
 * The lead's score is offered as a field too ("Score", under Scoring): the
 * engine works it out before any group is looked at, so a group or
 * destination can take only hot leads. It is a number, so it gets the
 * number conditions. With no scoring rules it is simply absent, as any
 * field a lead does not carry.
 *
 * The field list is read from the leads themselves (`/api/v1/lead-fields`):
 * the engine filters on any key a lead carries (top level, then
 * `custom_fields`), so the editor offers the built-in fields every routed
 * lead has, every field seen on recent leads with how many carry it, and the
 * described fields (whose label and type name a key). A key can still be typed
 * freely. A field with no option list suggests the values recent leads hold,
 * and with a campaign to try them on, the editor says how many recent leads
 * the filters would have stopped, worked out by the routing evaluator itself.
 *
 * A reference list filter (`campaign_match`) is built here too: the field on
 * the lead, the lists to look in, and optionally the field in the list when
 * its name differs. The engine turns every lead away when the field or the
 * list is missing, so the editor calls that unfinished and the group and link
 * forms refuse to save it (as does the server). The preview runs the real
 * lookups, and says so plainly when it could not.
 *
 * Two shapes taken from reading `filters.rs` rather than assumed:
 *
 *  - `config` is nested, and a filter with no `config` object makes the
 *    evaluator return "pass". Writing one without it does not disable the
 *    filter, it disables the *gate*. Every filter built here carries a config.
 *  - The config key differs per type: `buyer_history` writes `history_type`
 *    where `sold_to_buyer` writes `condition`. The key always comes from the
 *    served option.
 */
import { useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  ActionIcon, Alert, Anchor, Autocomplete, Button, Collapse, Grid, Group, MultiSelect, Select, Stack, Switch, Text,
  Tooltip, type ComboboxParsedItem, type OptionsFilter,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { useCampaigns, useEnums } from '../api/queries';
import { useProperties, type PropertyDef } from '../api/properties';
import {
  lastSeenLabel, useFilterPreview, useLeadFields, useLeadFieldValues, useReferenceListRows, type LeadField,
} from '../api/leadFields';
import { formatNumber } from '../theme';
import { useVocab, type Vocab } from '../vocab';
import { FilterValue } from './properties/FilterValue';
import { operatorLabel, operatorsFor } from './properties/propertyModel';
import { fieldMatches } from './leadsFilterModel';

type Enums = NonNullable<ReturnType<typeof useEnums>['data']>;
export type FilterType = Enums['filter_types'][number];

/** Where a field filter's field can be picked from: named groups, and the
 *  definitions that type them (for conditions and value inputs). */
export interface FieldSource {
  groups: { group: string; items: { value: string; label: string }[] }[];
  defs: PropertyDef[];
  /** A short note beside a field, such as how many leads carry it. */
  hints?: Record<string, string>;
  /** The share of sampled leads carrying each field, 0 to 1, where known. */
  coverage?: Record<string, number>;
  /** Suggest values seen on these campaigns' leads (every campaign when the
   *  list is empty). Without it, no values are suggested. */
  values?: { campaignIds: string[] };
}

/** A number field nobody described, typed as if it had been so the
 *  editor offers number conditions and a number input. */
export function syntheticNumberField(key: string, label: string): PropertyDef {
  return {
    id: `synthetic:${key}`,
    object_type: 'submission',
    key,
    label,
    type: 'number',
    options: [],
    group_name: '',
    position: 0,
    required: false,
    description: '',
    archived: false,
    created_at: '',
    updated_at: '',
    created_by: '',
  };
}

/** The lead's score, as routing filters read it. */
export const SCORE_FIELD = syntheticNumberField('score', 'Score');

/** What each built-in field is, in words. Keys are what the engine reads. */
export function builtInLabel(v: Vocab, key: string): string {
  switch (key) {
    case 'source': return 'Source';
    case 'supplier_id': return `${v.One('supplier')} id`;
    case 'supplier_name': return `${v.One('supplier')} name`;
    case 'email': return 'Email';
    case 'phone': return 'Phone';
    case 'name': return 'Name';
    case 'company': return 'Company';
    case 'revenue': return 'Revenue sent with it';
    case 'score': return 'Score';
    case 'sold': return 'Already sold (true or false)';
    default: return key;
  }
}

/**
 * One field in a field dropdown: its name in words as the label, or just the
 * key when the words say nothing more ("Source" for `source`). The dropdown
 * shows the key under the label when they differ, and never says where a
 * label came from.
 */
export function fieldItem(key: string, label?: string): { value: string; label: string } {
  const plain = (x: string) => x.toLowerCase().replace(/[_\s]+/g, ' ').trim();
  const words = label?.trim() || key;
  return { value: key, label: plain(words) === plain(key) ? key : words };
}

/** Typing in a field dropdown matches an option's label or its key. */
export const filterFieldOptions: OptionsFilter = ({ options, search }) =>
  options.flatMap((o): ComboboxParsedItem[] => {
    if ('group' in o) {
      const items = o.items.filter((i) => fieldMatches({ value: i.value, label: i.label }, search));
      return items.length ? [{ ...o, items }] : [];
    }
    return fieldMatches({ value: o.value, label: o.label }, search) ? [o] : [];
  });

/**
 * "On 64% of leads", "On fewer than 1% of leads", "Last seen 17 Apr" (in the
 * campaigns' all-time record but not on recent leads), "Not seen recently".
 */
export function coverageHint(
  v: Vocab,
  f: Pick<LeadField, 'coverage' | 'leads_with' | 'computed' | 'declared' | 'last_seen'>,
): string {
  if (f.computed) return 'Worked out when routing';
  if (f.leads_with > 0) {
    const pct = Math.round(f.coverage * 100);
    return pct < 1 ? `On fewer than 1% of ${v.many('lead')}` : `On ${pct}% of ${v.many('lead')}`;
  }
  if (f.last_seen) return `Last seen ${lastSeenLabel(f.last_seen)}`;
  return f.declared ? `Declared on the ${v.one('campaign')}, not seen recently` : 'Not seen recently';
}

/**
 * The fields a filter on a lead can name, in the same groups as the Leads
 * page: Common fields (the built-in ones every routed lead has, the score
 * among them), Seen on leads (recent leads of these campaigns, every
 * campaign when none, with how many carry each), Seen before (in the
 * campaigns' all-time record only) and Not seen recently. A described
 * field's label names its key. Until the leads have been read, the score
 * and the described fields.
 */
export function useLeadFieldSource(campaignIds?: string[], enabled = true): FieldSource {
  const v = useVocab();
  const seen = useLeadFields(campaignIds, enabled).data;
  const props = useProperties('submission', { enabled }).data?.properties;
  const ids = [...new Set(campaignIds ?? [])].sort().join(',');
  return useMemo(() => {
    const defs = props ?? [];
    const values = { campaignIds: ids ? ids.split(',') : [] };
    if (!seen) {
      return {
        groups: [
          { group: 'Common fields', items: [fieldItem(SCORE_FIELD.key, SCORE_FIELD.label)] },
          { group: 'Fields', items: defs.map((d) => fieldItem(d.key, d.label)) },
        ],
        defs: [...defs, SCORE_FIELD],
        values,
      };
    }
    const labelOf = (f: LeadField) => {
      const d = defs.find((x) => x.key === f.key);
      if (d) return d.label;
      return f.built_in ? builtInLabel(v, f.key) : f.key;
    };
    const hints: Record<string, string> = {};
    const coverage: Record<string, number> = {};
    for (const f of seen.fields) {
      hints[f.key] = coverageHint(v, f);
      if (f.leads_with > 0) coverage[f.key] = f.coverage;
    }
    const item = (f: LeadField) => fieldItem(f.key, labelOf(f));
    return {
      groups: [
        { group: 'Common fields', items: seen.fields.filter((f) => f.built_in).map(item) },
        { group: `Seen on ${v.many('lead')}`, items: seen.fields.filter((f) => !f.built_in && f.leads_with > 0).map(item) },
        // Not in the sample, but in the campaigns' all-time record.
        { group: 'Seen before', items: seen.fields.filter((f) => !f.built_in && f.leads_with === 0 && f.last_seen).map(item) },
        { group: 'Not seen recently', items: seen.fields.filter((f) => !f.built_in && f.leads_with === 0 && !f.last_seen).map(item) },
      ],
      defs: [...defs, SCORE_FIELD],
      hints,
      coverage,
      values,
    };
  }, [seen, props, ids, v]);
}

/**
 * Put `extra` after `own`, leaving out any key `own` already offers: the
 * dropdown cannot hold one value twice.
 */
export function mergeFieldSources(own: FieldSource, extra: FieldSource): FieldSource {
  const taken = new Set(own.groups.flatMap((g) => g.items.map((i) => i.value)));
  const groups = [
    ...own.groups,
    ...extra.groups.map((g) => {
      const items = g.items.filter((i) => !taken.has(i.value));
      items.forEach((i) => taken.add(i.value));
      return { ...g, items };
    }),
  ];
  const defKeys = new Set(own.defs.map((d) => d.key));
  return {
    groups,
    defs: [...own.defs, ...extra.defs.filter((d) => !defKeys.has(d.key))],
    hints: { ...extra.hints, ...own.hints },
    coverage: { ...extra.coverage, ...own.coverage },
    values: own.values ?? extra.values,
  };
}

export type SmartFilter = {
  type: string;
  enabled: boolean;
  logic: 'and' | 'or';
  config: Record<string, string>;
};

/**
 * The `field` and `campaign_match` filters take a field name; no other type
 * does. The enums endpoint describes which operators a field filter accepts
 * but not that it needs a `field` key at all, so this one mapping is not served.
 *
 * It is the only hand-written assumption in this file, and it is written down
 * rather than buried so it stays visible. The clean fix is adding the
 * free-text keys to the served `FilterType`, after which this constant goes.
 */
const TYPES_WITH_FIELD_NAME = new Set(['field', 'campaign_match']);

/** The stored key of the reference list filter. Its label is served. */
const LIST_FILTER = 'campaign_match';

/**
 * The lists a reference list filter looks in. The one value in a filter's
 * config that is not text: stored as an array, and the engine also reads a
 * comma-separated string, as an older save may hold.
 */
export function listIdsOf(config: SmartFilter['config']): string[] {
  const raw = (config as Record<string, unknown>).campaign_ids;
  const parts = Array.isArray(raw) ? (raw as unknown[]) : typeof raw === 'string' ? raw.split(',') : [];
  return [...new Set(parts.map((x) => String(x).trim()).filter(Boolean))];
}

function withListIds(config: SmartFilter['config'], ids: string[]): SmartFilter['config'] {
  return { ...config, campaign_ids: ids as unknown as string };
}

/** A name the list lookup can use, as `ch_value_exprs` on the server allows. */
const LOOKUP_NAME = /^[A-Za-z_][A-Za-z0-9_]*$/;

/**
 * What stops a reference list filter running, in words. The engine turns every
 * lead away in each of these cases; the server refuses the same ones on save.
 */
export function listFilterProblem(f: SmartFilter): string | null {
  if (f.type !== LIST_FILTER) return null;
  const field = (f.config.field ?? '').trim();
  const lists = listIdsOf(f.config).length > 0;
  if (!field && !lists) return 'has no field and no list';
  if (!field) return 'has no field';
  if (!lists) return 'has no list';
  const lookup = (f.config.match_field ?? '').trim() || field;
  if (!LOOKUP_NAME.test(lookup)) return `looks in "${lookup}", which can only use letters, digits and underscores`;
  return null;
}

/** Reference list filters that cannot run. Saving a route is refused while there are any. */
export function countUnfinishedLists(filters: SmartFilter[]): number {
  return filters.filter((f) => listFilterProblem(f) !== null).length;
}

/** The sentence beside a form's Save, when a list filter holds it back. */
export function unfinishedListsReason(filters: SmartFilter[]): string | null {
  return countUnfinishedLists(filters) > 0
    ? 'Finish the reference list filter first: pick a field and at least one list.'
    : null;
}

/** Build a filter of `type` carrying the defaults the engine itself assumes. */
export function blankFilter(ft: FilterType, logic: 'and' | 'or'): SmartFilter {
  const config: Record<string, string> = {};
  for (const opt of ft.options) {
    config[opt.key] = opt.default ?? opt.values[0]?.value ?? '';
  }
  if (TYPES_WITH_FIELD_NAME.has(ft.value)) config.field = '';
  if (ft.options.some((o) => o.free_text)) config.value = '';
  const filter: SmartFilter = { type: ft.value, enabled: true, logic, config };
  return ft.value === LIST_FILTER ? { ...filter, config: withListIds(config, []) } : filter;
}

/** Read the stored filter array, keeping only one logic block. */
export function asFilters(raw: unknown, logic: 'and' | 'or'): SmartFilter[] {
  if (!Array.isArray(raw)) return [];
  return (raw as Record<string, unknown>[])
    .filter((f) => ((f.logic as string) ?? 'and') === logic)
    .map((f) => ({
      type: (f.type as string) ?? 'field',
      enabled: f.enabled !== false,
      logic,
      config: { ...((f.config as Record<string, string>) ?? {}) },
    }));
}

/** Field filters with no field name compare against nothing and reject everything. */
function countFieldless(filters: SmartFilter[]): number {
  return filters.filter((f) => f.type !== LIST_FILTER && TYPES_WITH_FIELD_NAME.has(f.type) && !f.config.field?.trim()).length;
}

/** Filters that would turn everything away because they were left unfinished. */
export function countIncomplete(filters: SmartFilter[]): number {
  return countFieldless(filters) + countUnfinishedLists(filters);
}

/**
 * The field box of a filter: fields by label with how many leads carry each,
 * and free typing. A picked option fills the box with its label, but what is
 * stored is the key.
 */
export function FieldInput({
  label, placeholder, description, fields, value, onChange, onPick, testId,
}: {
  label: string;
  placeholder?: string;
  description?: string;
  fields: FieldSource;
  value: string;
  onChange: (key: string) => void;
  /** Told when a field is picked from the list, as opposed to typed. */
  onPick?: (key: string) => void;
  testId: string;
}) {
  const picked = useRef<string | null>(null);
  return (
    <Autocomplete
      label={label}
      size="xs"
      placeholder={placeholder}
      description={description}
      data={fields.groups.filter((g) => g.items.length > 0)}
      maxDropdownHeight={300}
      comboboxProps={{ width: 320, position: 'bottom-start' }}
      filter={filterFieldOptions}
      renderOption={({ option }) => {
        const text = (option as { label?: string }).label ?? option.value;
        // The key under the label only when the label is not just the key.
        const under = [text !== option.value ? option.value : '', fields.hints?.[option.value] ?? '']
          .filter(Boolean)
          .join(' · ');
        return (
          <div>
            <Text size="xs">{text}</Text>
            {under && <Text size="xs" c="dimmed">{under}</Text>}
          </div>
        );
      }}
      value={value}
      // Picking an option puts its label in the box; the filter stores
      // the key, so the picked key wins over the label that follows.
      onOptionSubmit={(key) => { picked.current = key; onPick?.(key); }}
      onChange={(f) => {
        const key = picked.current ?? f;
        picked.current = null;
        onChange(key);
      }}
      data-testid={testId}
    />
  );
}

/** One choice in the list picker. */
export type ListOption = { value: string; label: string; note?: string };

/**
 * What the list picker offers: reference lists by name, with their size where
 * known. A saved filter may name a stream that is not a reference list, or one
 * that is gone; those stay on show, marked, so a save never loses them, but
 * only reference lists are offered for a new pick.
 */
export function listOptions(
  v: Vocab,
  campaigns: { id: string; name: string; is_reference_list: boolean }[],
  rows: { campaign_id: string; rows: number }[],
  picked: string[],
): ListOption[] {
  const size = (id: string) => {
    const n = rows.find((r) => r.campaign_id === id)?.rows;
    return n === undefined ? undefined : v.count(n, 'lead');
  };
  const lists = campaigns
    .filter((c) => c.is_reference_list)
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((c): ListOption => ({ value: c.id, label: c.name, note: size(c.id) }));
  const kept = picked
    .filter((id) => !lists.some((l) => l.value === id))
    .map((id): ListOption => {
      const c = campaigns.find((x) => x.id === id);
      return c
        ? { value: id, label: `${c.name} (not a reference list)`, note: `A ${v.one('campaign')}, not a reference list` }
        : { value: id, label: `Unknown list (${id})`, note: 'Deleted, or not one this login can see' };
    });
  return [...kept, ...lists];
}

/** What happens to a lead with an empty field, from how many leads have it. */
export function emptyFieldNote(v: Vocab, coverage: number | undefined, onMissing: string): string {
  const fate = onMissing === 'pass' ? 'would go through unchecked' : 'would be stopped';
  if (coverage === undefined) {
    return `${v.Many('lead')} with nothing in this field ${fate}.`;
  }
  if (coverage >= 1) return `Every recent ${v.one('lead')} has this field.`;
  const pct = Math.max(1, Math.round((1 - coverage) * 100));
  return `${pct}% of recent ${v.many('lead')} have nothing in this field, so they ${fate}.`;
}

/** The fields of the lists themselves, for the field in the list. */
function useListFieldSource(ids: string[]): { source: FieldSource; has: (key: string) => boolean | null } {
  const v = useVocab();
  const { data } = useLeadFields(ids, ids.length > 0);
  return useMemo(() => {
    const own = (data?.fields ?? []).filter((f) => f.leads_with > 0 && !f.computed);
    const hints: Record<string, string> = {};
    for (const f of own) hints[f.key] = coverageHint(v, f);
    return {
      source: {
        groups: [{ group: ids.length === 1 ? 'In this list' : 'In these lists', items: own.map((f) => fieldItem(f.key)) }],
        defs: [],
        hints,
      },
      // Unknown until a sample has come back with rows in it.
      has: (key: string) => (data && data.sample_rows > 0 ? own.some((f) => f.key === key) : null),
    };
  }, [data, ids.length, v]);
}

/** A reference list filter's controls: which field, in which lists, and what if it is empty. */
function ListFilterRow({
  filter, ft, fields, typeSelect, controls, onChange,
}: {
  filter: SmartFilter;
  ft: FilterType | undefined;
  fields: FieldSource;
  typeSelect: ReactNode;
  controls: ReactNode;
  onChange: (f: SmartFilter) => void;
}) {
  const v = useVocab();
  const config = filter.config;
  const set = (key: string, value: string) => onChange({ ...filter, config: { ...config, [key]: value } });
  const field = (config.field ?? '').trim();
  const ids = listIdsOf(config);
  const campaigns = useCampaigns();
  const rows = useReferenceListRows();
  const options = listOptions(v, campaigns.data?.campaigns ?? [], rows.data?.lists ?? [], ids);
  const notes = Object.fromEntries(options.map((o) => [o.value, o.note]));
  const noLists = !!campaigns.data && options.length === 0;

  const matchField = (config.match_field ?? '').trim();
  const list = useListFieldSource(ids);
  const lookedFor = matchField || field;
  const notInList = !!lookedFor && list.has(lookedFor) === false;
  const [advanced, setAdvanced] = useState(!!matchField);
  const showAdvanced = advanced || notInList;

  const conditionOpt = ft?.options.find((o) => o.key === 'condition');
  const missingOpt = ft?.options.find((o) => o.key === 'on_missing');
  const onMissing = config.on_missing ?? missingOpt?.default ?? 'block';
  const def = fields.defs.find((d) => d.key === field);

  return (
    <Stack gap={6} data-testid="list-filter-row">
      <Grid gutter="xs" align="flex-end">
        <Grid.Col span={{ base: 12, sm: 3 }}>{typeSelect}</Grid.Col>
        <Grid.Col span={{ base: 12, sm: 4 }}>
          <FieldInput
            label="Field"
            placeholder="postcode"
            description={[def?.label, fields.hints?.[field]].filter(Boolean).join(' · ') || undefined}
            fields={fields}
            value={config.field ?? ''}
            onChange={(key) => set('field', key)}
            testId="list-filter-field"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 4 }}>
          {conditionOpt && (
            <Select
              label={conditionOpt.label}
              size="xs"
              data={conditionOpt.values.map((x) => ({ value: x.value, label: x.label }))}
              value={config.condition ?? conditionOpt.default ?? null}
              allowDeselect={false}
              onChange={(x) => x && set('condition', x)}
            />
          )}
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 1 }}>{controls}</Grid.Col>
      </Grid>
      <Grid gutter="xs" align="flex-start">
        <Grid.Col span={{ base: 12, sm: 7 }}>
          <MultiSelect
            label="Lists"
            size="xs"
            searchable
            disabled={noLists}
            placeholder={noLists ? 'No reference lists yet' : ids.length === 0 ? 'Choose a list' : undefined}
            nothingFoundMessage="No list matches"
            data={options.map((o) => ({ value: o.value, label: o.label }))}
            value={ids}
            onChange={(next) => onChange({ ...filter, config: withListIds(config, next) })}
            maxDropdownHeight={280}
            comboboxProps={{ position: 'bottom-start' }}
            renderOption={({ option }) => (
              <div>
                <Text size="xs">{option.label}</Text>
                {notes[option.value] && <Text size="xs" c="dimmed">{notes[option.value]}</Text>}
              </div>
            )}
            data-testid="list-filter-lists"
          />
          {noLists && (
            <Text size="xs" c="dimmed" mt={4} data-testid="list-filter-empty">
              {'No reference lists yet. '}
              <Anchor component={Link} to="/campaigns?new=1" size="xs">Create one</Anchor>
            </Text>
          )}
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 5 }}>
          {missingOpt && (
            <Select
              label={missingOpt.label}
              size="xs"
              data={missingOpt.values.map((x) => ({ value: x.value, label: x.label }))}
              value={onMissing}
              allowDeselect={false}
              onChange={(x) => x && set('on_missing', x)}
            />
          )}
          {field && (
            <Text size="xs" c="dimmed" mt={4} data-testid="list-filter-empty-note">
              {emptyFieldNote(v, fields.coverage?.[field], onMissing)}
            </Text>
          )}
        </Grid.Col>
      </Grid>
      <Text size="xs" c="dimmed">
        {`Compares this field on each ${v.one('lead')} with the same field in the lists you pick. Make a list by marking a ${v.one('campaign')} as a reference list.`}
      </Text>
      {!showAdvanced && (
        <Anchor
          component="button"
          type="button"
          size="xs"
          ta="left"
          onClick={() => setAdvanced(true)}
          data-testid="list-filter-advanced"
        >
          The field has a different name in the list
        </Anchor>
      )}
      <Collapse in={showAdvanced}>
        <Grid gutter="xs">
          <Grid.Col span={{ base: 12, sm: 7 }}>
            <FieldInput
              label="Field in the list"
              placeholder={field ? `Same name: ${field}` : 'The same name'}
              description={
                (matchField && list.source.hints?.[matchField]) || 'Leave empty to look for the same name.'
              }
              fields={list.source}
              value={config.match_field ?? ''}
              onChange={(key) => {
                if (key.trim()) return set('match_field', key);
                const rest = { ...config };
                delete rest.match_field;
                onChange({ ...filter, config: rest });
              }}
              testId="list-filter-match-field"
            />
          </Grid.Col>
        </Grid>
      </Collapse>
      {notInList && (
        <Text size="xs" c="chartAmber" data-testid="list-filter-not-in-list">
          {`Nothing in ${ids.length === 1 ? 'this list' : 'these lists'} has a field called ${lookedFor}, so no value will be found. Pick the field in the list above.`}
        </Text>
      )}
    </Stack>
  );
}

function FilterRow({
  filter,
  types,
  fields,
  onChange,
  onRemove,
}: {
  filter: SmartFilter;
  types: FilterType[];
  /** The fields a field filter can pick, and the definitions typing them. */
  fields: FieldSource;
  onChange: (f: SmartFilter) => void;
  onRemove: () => void;
}) {
  const props = fields.defs;
  const ft = types.find((t) => t.value === filter.type);
  const set = (key: string, value: string) =>
    onChange({ ...filter, config: { ...filter.config, [key]: value } });
  const isField = filter.type === 'field';
  const fieldName = (filter.config.field ?? '').trim();
  const def = isField ? (props.find((d) => d.key === fieldName) ?? null) : null;
  const hint = fields.hints?.[fieldName];
  // The values recent leads hold, for a field with no option list of its own.
  const [typedField] = useDebouncedValue(fieldName, 300);
  const wantsValues = isField && !!fields.values && !def?.options.length && def?.type !== 'boolean';
  const suggested = useLeadFieldValues(typedField, fields.values?.campaignIds, wantsValues).data?.values;
  /** A field filter's conditions, narrowed to its field's type. */
  const valuesOf = (opt: FilterType['options'][number]) => {
    if (!isField || opt.key !== 'operator' || !def) return opt.values.map((x) => ({ value: x.value, label: x.label }));
    const allowed = operatorsFor(def.type, opt.values.map((x) => x.value));
    const current = filter.config[opt.key];
    return opt.values
      .filter((x) => allowed.includes(x.value) || x.value === current)
      .map((x) => ({ value: x.value, label: operatorLabel(def.type, x.value, x.label) }));
  };

  const typeSelect = (
    <Select
      label="Type"
      size="xs"
      data={types.map((t) => ({ value: t.value, label: t.label }))}
      value={filter.type}
      allowDeselect={false}
      onChange={(v) => {
        const next = types.find((t) => t.value === v);
        // Changing type replaces the config wholesale: the keys differ per
        // type, so carrying old ones over leaves the filter holding keys
        // its evaluator never reads.
        if (next) onChange(blankFilter(next, filter.logic));
      }}
    />
  );
  const controls = (
    <Group gap={4} wrap="nowrap">
      <Tooltip label={filter.enabled ? 'Filter is on' : 'Filter is off and will be ignored'}>
        <Switch
          size="xs"
          checked={filter.enabled}
          onChange={(e) => onChange({ ...filter, enabled: e.currentTarget.checked })}
        />
      </Tooltip>
      <ActionIcon variant="subtle" color="red" onClick={onRemove} aria-label="Remove filter">
        <IconTrash size={15} />
      </ActionIcon>
    </Group>
  );

  if (filter.type === LIST_FILTER) {
    return (
      <ListFilterRow
        filter={filter}
        ft={ft}
        fields={fields}
        typeSelect={typeSelect}
        controls={controls}
        onChange={onChange}
      />
    );
  }

  return (
    <Grid gutter="xs" align="flex-end">
      <Grid.Col span={{ base: 12, sm: 3 }}>{typeSelect}</Grid.Col>

      {isField && (
        <Grid.Col span={{ base: 12, sm: 3 }}>
          <FieldInput
            label="Field"
            placeholder="marketing_consent_email"
            description={[def?.label, hint].filter(Boolean).join(' · ') || undefined}
            fields={fields}
            value={filter.config.field ?? ''}
            onChange={(key) => set('field', key)}
            testId="smart-filter-field"
          />
        </Grid.Col>
      )}

      {(ft?.options ?? []).map((opt) => (
        <Grid.Col span={{ base: 12, sm: 3 }} key={opt.key}>
          <Select
            label={opt.label}
            size="xs"
            data={valuesOf(opt)}
            value={filter.config[opt.key] ?? opt.default ?? null}
            allowDeselect={false}
            onChange={(x) => x && set(opt.key, x)}
          />
        </Grid.Col>
      ))}

      {(ft?.options ?? []).some((o) => o.free_text) && (
        <Grid.Col span={{ base: 12, sm: 2 }}>
          <FilterValue
            label="Value"
            showLabel
            size="xs"
            def={def}
            operator={filter.config.operator ?? ''}
            value={filter.config.value ?? ''}
            onChange={(x) => set('value', x)}
            suggestions={wantsValues && typedField === fieldName ? suggested : undefined}
          />
        </Grid.Col>
      )}

      <Grid.Col span={{ base: 12, sm: 1 }}>{controls}</Grid.Col>
    </Grid>
  );
}

function FilterBlock({
  title,
  hint,
  logic,
  filters,
  types,
  fields,
  onChange,
}: {
  title: string;
  hint: string;
  logic: 'and' | 'or';
  filters: SmartFilter[];
  types: FilterType[];
  fields: FieldSource;
  onChange: (next: SmartFilter[]) => void;
}) {
  const add = () => {
    const first = types[0];
    if (first) onChange([...filters, blankFilter(first, logic)]);
  };
  return (
    <Stack gap="xs">
      <Group justify="space-between">
        <div>
          <Text size="sm" fw={600}>{title}</Text>
          <Text size="xs" c="dimmed">{hint}</Text>
        </div>
        <Button size="compact-xs" variant="light" leftSection={<IconPlus size={13} />} onClick={add}>
          Add
        </Button>
      </Group>
      {filters.length === 0 ? (
        <Text size="xs" c="dimmed">None.</Text>
      ) : (
        filters.map((f, i) => (
          <FilterRow
            key={i}
            filter={f}
            types={types}
            fields={fields}
            onChange={(next) => onChange(filters.map((x, j) => (j === i ? next : x)))}
            onRemove={() => onChange(filters.filter((_, j) => j !== i))}
          />
        ))
      )}
    </Stack>
  );
}

/**
 * How many of the campaign's recent leads these filters would have stopped,
 * counted on the server by the routing evaluator over the newest leads.
 */
function FilterPreview({
  campaignId,
  filters,
  types,
}: {
  campaignId: string;
  filters: ReturnType<typeof toStored>;
  types: FilterType[];
}) {
  const v = useVocab();
  const [debounced] = useDebouncedValue(filters, 500);
  const { data, isError, isFetching } = useFilterPreview(campaignId, debounced);
  if (isError) {
    return <Text size="xs" c="dimmed">{`Could not check these against recent ${v.many('lead')}.`}</Text>;
  }
  if (!data) {
    return <Text size="xs" c="dimmed">{`Checking recent ${v.many('lead')}...`}</Text>;
  }
  const typeLabel = (t: string) => types.find((x) => x.value === t)?.label ?? t;
  const listsSkipped = data.skipped_types.includes(LIST_FILTER);
  const otherSkipped = data.skipped_types.filter((t) => t !== LIST_FILTER);
  const pct = data.sample_rows > 0 ? Math.round((data.blocked / data.sample_rows) * 100) : 0;
  const window = data.capped
    ? `the last ${formatNumber(data.sample_rows)} ${v.many('lead')}`
    : `the ${formatNumber(data.sample_rows)} ${data.sample_rows === 1 ? v.one('lead') : v.many('lead')} in the last ${data.days} days`;
  return (
    <Alert
      // Amber only when nothing would get through: that is usually a mistake.
      color={data.sample_rows > 0 && data.blocked === data.sample_rows ? 'chartAmber' : 'chartBlue'}
      variant="light"
      data-testid="filter-preview"
      style={{ opacity: isFetching ? 0.7 : 1 }}
    >
      <Stack gap={4}>
        <Text size="sm">
          {data.sample_rows === 0
            ? `No ${v.many('lead')} in the last ${data.days} days to try these on.`
            : `This would have stopped ${formatNumber(data.blocked)} of ${window} (${pct}%).`}
        </Text>
        {data.reasons[0] && data.blocked > 0 && (
          <Text size="xs" c="dimmed">{`Most often: ${data.reasons[0].reason}`}</Text>
        )}
        {otherSkipped.length > 0 && (
          <Text size="xs" c="dimmed">
            {`Not counted: ${otherSkipped.map(typeLabel).join(', ')}. These depend on history or the moment a ${v.one('lead')} arrives.`}
          </Text>
        )}
        {listsSkipped && (
          <Text size="xs" c="dimmed" data-testid="filter-preview-lists-skipped">
            {`Not counted: ${typeLabel(LIST_FILTER)}. A list could not be checked just now, so the real figure may be higher.`}
          </Text>
        )}
      </Stack>
    </Alert>
  );
}

/** Both logic blocks, plus the warnings that matter about them. */
export function SmartFilters({
  andFilters,
  orFilters,
  enabled,
  setAndFilters,
  setOrFilters,
  allowTypes,
  fields: given,
  noun,
  campaignIds,
  previewCampaignId,
}: {
  andFilters: SmartFilter[];
  orFilters: SmartFilter[];
  enabled: boolean;
  setAndFilters: (f: SmartFilter[]) => void;
  setOrFilters: (f: SmartFilter[]) => void;
  /** Offer only these filter types, e.g. email audiences, which can only
   *  read the lead itself. All types when omitted. */
  allowTypes?: string[];
  /** The fields to offer instead of the lead's fields and its score
   *  (a scoring rule about people offers what a person has). */
  fields?: FieldSource;
  /** What passes or is turned away, in the warnings. A lead when omitted. */
  noun?: string;
  /** The campaigns whose leads the fields and value suggestions are read
   *  from, when `fields` is not given. Every campaign when omitted. */
  campaignIds?: string[];
  /** Say how many of this campaign's recent leads the filters would have
   *  stopped. No preview when omitted. */
  previewCampaignId?: string;
}) {
  const v = useVocab();
  const { data: enums } = useEnums();
  const types = (enums?.filter_types ?? []).filter((t) => !allowTypes || allowTypes.includes(t.value));
  const leadSource = useLeadFieldSource(campaignIds, !given);
  const fields: FieldSource = given ?? leadSource;
  const what = noun ?? v.one('lead');
  const all = [...andFilters, ...orFilters];
  const fieldless = countFieldless(all);
  const unfinishedLists = countUnfinishedLists(all);
  const total = andFilters.length + orFilters.length;

  return (
    <>
      {!enabled && total > 0 && (
        <Alert color="chartAmber" variant="light">
          <Text size="sm">
            {`${total} ${total === 1 ? 'filter is' : 'filters are'} configured but filtering is switched off, so every ${what} reaching here passes them.`}
          </Text>
        </Alert>
      )}

      <FilterBlock
        title="Match all (AND)"
        hint="Every filter here must pass."
        logic="and"
        filters={andFilters}
        types={types}
        fields={fields}
        onChange={setAndFilters}
      />
      <FilterBlock
        title="Match any (OR)"
        hint="At least one of these must pass."
        logic="or"
        filters={orFilters}
        types={types}
        fields={fields}
        onChange={setOrFilters}
      />

      {(fieldless > 0 || unfinishedLists > 0) && (
        <Alert color="chartAmber" variant="light" title="Unfinished filters" data-testid="unfinished-filters">
          <Stack gap={4}>
            {fieldless > 0 && (
              <Text size="sm">
                {`${fieldless} ${fieldless === 1 ? 'field filter has' : 'field filters have'} no field name. A field filter with an empty field compares against nothing and will turn every ${what} away.`}
              </Text>
            )}
            {all.flatMap((f, i) => {
              const problem = listFilterProblem(f);
              return problem ? [(
                <Text size="sm" key={i}>
                  {`A reference list filter ${problem}. It would turn every ${what} away, so it cannot be saved like this.`}
                </Text>
              )] : [];
            })}
          </Stack>
        </Alert>
      )}

      {previewCampaignId && enabled && total > 0 && unfinishedLists === 0 && (
        <FilterPreview
          campaignId={previewCampaignId}
          filters={toStored(andFilters, orFilters)}
          types={enums?.filter_types ?? []}
        />
      )}
    </>
  );
}

/** Flatten both blocks back into the array the engine stores. */
export function toStored(andFilters: SmartFilter[], orFilters: SmartFilter[]) {
  return [
    ...andFilters.map((f) => ({ ...f, logic: 'and' })),
    ...orFilters.map((f) => ({ ...f, logic: 'or' })),
  ];
}
